"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import type { DropInteractionParams } from "@/components/waves/drops/Drop";
import { DropLocation } from "@/components/waves/drops/Drop";
import DropMobileMenuHandler from "@/components/waves/drops/DropMobileMenuHandler";
import WaveDropReactions from "@/components/waves/drops/WaveDropReactions";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useCallback, useState } from "react";
import MemeDropActions from "./meme-participation-drop/MemeDropActions";
import MemeDropArtistInfo from "./meme-participation-drop/MemeDropArtistInfo";
import MemeDropDescription from "./meme-participation-drop/MemeDropDescription";
import MemeDropHeader from "./meme-participation-drop/MemeDropHeader";
import MemeDropVoteStats from "./meme-participation-drop/MemeDropVoteStats";
import MemeDropTraits from "./MemeDropTraits";
interface MemeParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
}

// Border styling based on rank
const getBorderClasses = (drop: ExtendedDrop, isActiveDrop: boolean) => {
  const rank = drop.rank && drop.rank <= 3 ? drop.rank : null;
  const baseClasses = "tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-transition-all tw-duration-200 tw-ease-out tw-overflow-hidden";
  if (isActiveDrop) {
    return `${baseClasses} desktop-hover:hover:tw-border-[#3CCB7F]/40 tw-bg-[#3CCB7F]/5`;
  } else if (rank === 1) {
    return `${baseClasses} desktop-hover:hover:tw-border-[#fbbf24]/40`;
  } else if (rank === 2) {
    return `${baseClasses} desktop-hover:hover:tw-border-[#94a3b8]/40`;
  } else if (rank === 3) {
    return `${baseClasses} desktop-hover:hover:tw-border-[#CD7F32]/40`;
  } else {
    return `${baseClasses} tw-border-iron-800`;
  }
};
export default function MemeParticipationDrop(t0) {
  const $ = _c(74);
  const {
    drop,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply
  } = t0;
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  let t1;
  if ($[0] !== drop.metadata) {
    t1 = drop.metadata?.find(_temp)?.data_value ?? "Artwork Title";
    $[0] = drop.metadata;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const title = t1;
  let t2;
  if ($[2] !== drop.metadata) {
    t2 = drop.metadata?.find(_temp2)?.data_value ?? "This is an artwork submission for The Memes collection.";
    $[2] = drop.metadata;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const description = t2;
  let t3;
  if ($[4] !== drop.parts) {
    t3 = drop.parts.at(0)?.media.at(0);
    $[4] = drop.parts;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const artworkMedia = t3;
  let t4;
  if ($[6] !== drop || $[7] !== isActiveDrop) {
    t4 = getBorderClasses(drop, isActiveDrop);
    $[6] = drop;
    $[7] = isActiveDrop;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const borderClasses = t4;
  let t5;
  if ($[9] !== drop || $[10] !== onReply) {
    t5 = () => {
      onReply({
        drop,
        partId: drop.parts[0]?.part_id
      });
    };
    $[9] = drop;
    $[10] = onReply;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleOnReply = t5;
  const t6 = `tw-group tw-relative tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
  const t7 = `${borderClasses} ${location === DropLocation.WAVE ? "tw-bg-iron-900/80" : "tw-bg-iron-950"}`;
  let t8;
  if ($[12] !== drop) {
    t8 = <MemeDropArtistInfo drop={drop} />;
    $[12] = drop;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== title) {
    t9 = <MemeDropHeader title={title} />;
    $[14] = title;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== description) {
    t10 = <MemeDropDescription description={description} />;
    $[16] = description;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <div className="tw-mt-2 tw-flex tw-flex-col sm:tw-ml-[3.25rem] sm:tw-mt-1.5">{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t8) {
    t12 = <div className="tw-p-4">{t8}{t11}</div>;
    $[21] = t11;
    $[22] = t8;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== artworkMedia || $[25] !== location) {
    t13 = artworkMedia && <div className={`tw-flex tw-h-96 tw-justify-center ${location === DropLocation.WAVE ? "tw-bg-iron-800/30" : "tw-bg-iron-900/40"}`}><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
    $[24] = artworkMedia;
    $[25] = location;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== drop) {
    t14 = <div className="tw-px-4 tw-py-4"><MemeDropTraits drop={drop} /></div>;
    $[27] = drop;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t12 || $[30] !== t13 || $[31] !== t14) {
    t15 = <>{t12}{t13}{t14}</>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== drop || $[34] !== handleOnReply || $[35] !== showReplyAndQuote || $[36] !== t15) {
    t16 = <DropMobileMenuHandler drop={drop} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply}>{t15}</DropMobileMenuHandler>;
    $[33] = drop;
    $[34] = handleOnReply;
    $[35] = showReplyAndQuote;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== drop.top_raters) {
    t17 = drop.top_raters ?? [];
    $[38] = drop.top_raters;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== drop.context_profile_context || $[41] !== drop.raters_count || $[42] !== drop.rating || $[43] !== drop.rating_prediction || $[44] !== drop.wave.voting_credit_type || $[45] !== t17) {
    t18 = <div className="tw-px-4 @[700px]:tw-px-0"><MemeDropVoteStats current={drop.rating} projected={drop.rating_prediction} votingCreditType={drop.wave.voting_credit_type} ratersCount={drop.raters_count} topVoters={t17} userContext={drop.context_profile_context} /></div>;
    $[40] = drop.context_profile_context;
    $[41] = drop.raters_count;
    $[42] = drop.rating;
    $[43] = drop.rating_prediction;
    $[44] = drop.wave.voting_credit_type;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== canShowVote || $[48] !== drop) {
    t19 = canShowVote && <div className="tw-flex tw-w-full tw-justify-center tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-px-6 tw-pt-4 @[700px]:tw-w-auto @[700px]:tw-border-none @[700px]:tw-px-0 @[700px]:tw-pt-0"><div onClick={_temp3}><VotingModalButton drop={drop} onClick={() => setIsVotingModalOpen(true)} /></div></div>;
    $[47] = canShowVote;
    $[48] = drop;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t18 || $[51] !== t19) {
    t20 = <div className="tw-flex tw-flex-col tw-justify-between tw-gap-3 tw-pb-4 @[700px]:tw-flex-row @[700px]:tw-items-center @[700px]:tw-gap-y-0 @[700px]:tw-px-4">{t18}{t19}</div>;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== drop) {
    t21 = <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-px-4 tw-pb-4"><WaveDropReactions drop={drop} /></div>;
    $[53] = drop;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== drop || $[56] !== handleOnReply || $[57] !== isMobile || $[58] !== showReplyAndQuote) {
    t22 = <div className="tw-absolute tw-right-4 tw-top-2"><MemeDropActions drop={drop} isMobile={isMobile} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply} /></div>;
    $[55] = drop;
    $[56] = handleOnReply;
    $[57] = isMobile;
    $[58] = showReplyAndQuote;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t16 || $[61] !== t20 || $[62] !== t21 || $[63] !== t22 || $[64] !== t7) {
    t23 = <div className={t7}>{t16}{t20}{t21}{t22}</div>;
    $[60] = t16;
    $[61] = t20;
    $[62] = t21;
    $[63] = t22;
    $[64] = t7;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== drop || $[67] !== isMobileScreen || $[68] !== isVotingModalOpen) {
    t24 = isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />;
    $[66] = drop;
    $[67] = isMobileScreen;
    $[68] = isVotingModalOpen;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== t23 || $[71] !== t24 || $[72] !== t6) {
    t25 = <div className="tw-w-full tw-@container"><div className={t6}>{t23}{t24}</div></div>;
    $[70] = t23;
    $[71] = t24;
    $[72] = t6;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  return t25;
}
function _temp3(e) {
  return e.stopPropagation();
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp(m) {
  return m.data_key === "title";
}